import { Link } from "react-router-dom";
import { PageHero, SectionHeading, Reveal } from "@/components/Section";
import { COMPANY, IMAGES } from "@/data/site";
import { Check, ArrowRight, BadgeCheck } from "lucide-react";

const ACCREDITATIONS = [
  ["CIDB REGISTERED", "Construction Industry Development Board"],
  ["CSD REGISTERED", "Central Supplier Database — Government Supplier"],
  ["SARS TAX COMPLIANT", "Tax clearance & VAT compliant"],
  ["B-BBEE CERTIFIED", "Broad-Based Black Economic Empowerment certified"],
  ["CIPC REGISTERED", `Reg No: ${COMPANY.reg}`],
  ["EXPORT CERTIFIED", "Documentation-ready for cross-border trading"],
];
const MILESTONES = [
  ["2003","Established and headquartered in Pretoria"],
  ["2005","First major retail chain contract"],
  ["2010","Mining & industrial division launched"],
  ["2015","Export division activated, first international clients"],
  ["2018","Renovation & Construction division launched"],
  ["2023","20-year milestone · 500+ categories"],
  ["2025","Active trading clients across Africa"],
  ["TODAY","One supplier. Foundation to roof."],
];
const VALUES = [
  ["INNOVATION","We track every change in hardware, construction and mining. If it's new and relevant, we add it."],
  ["EXCELLENCE","We don't ship substandard products. Every order is a reflection of our 23-year reputation."],
  ["INTEGRITY","We say what we do and do what we say. In 23 years, our word has been our bond."],
];
const DIFFERENTIATORS = [
  ["WE HAVE EVERYTHING","500+ product categories. Foundation to roof. Mining to heavy equipment. One supplier."],
  ["WE'VE BEEN HERE 23 YEARS","We're not a startup. We have deep supplier relationships, industry knowledge and a track record."],
  ["WE TRADE ACROSS AFRICA","Our trading operations cover the continent — with established export clients in Malawi, Tanzania and across Africa."],
  ["WE ALSO BUILD","Our Renovation & Construction division means we supply AND build. Unique in our market."],
];

const StorySection = () => (
  <section className="relative bg-[#F8F6F2] py-24 overflow-hidden" data-testid="about-story">
    <div className="watermark-number select-none">23</div>
    <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-7">
        <SectionHeading label="OUR STORY" title="TRUSTED FOR 23 YEARS." />
        <div className="space-y-5 text-[#1E2D40] text-[17px] leading-7">
          <p>Cassie Gee Investments CC was established in 2003 and headquartered in Pretoria since 2003. Over 23 years we have grown into a full-range trading and supply company serving contractors, hardware retailers and international importers across Africa and beyond.</p>
          <p>Today we supply hardware retailers, construction companies, mining operations and international importers. Our clients include leading hardware and construction chains — Build It, Mica, SI Hardware and Dada's World of Hardware — alongside established export clients in Malawi, Tanzania and across Africa.</p>
          <p>Every decision we make comes back to three things: <strong>quality products, reliable delivery, and pricing that makes sense for your business.</strong></p>
        </div>
        <img src={IMAGES.contractor} alt="Construction team reviewing plans on site" loading="lazy" className="mt-8 w-full h-[280px] object-cover border-l-[3px] border-[#E8721C]" />
      </div>
      <div className="lg:col-span-5">
        <div className="bg-[#0A1628] text-white p-8 border-l-[3px] border-[#C8973C]" data-testid="credentials-box">
          <div className="text-[12px] tracking-[.22em] text-[#C8973C] font-bold">COMPANY CREDENTIALS</div>
          <h3 className="font-display text-[28px] mt-3 leading-tight">OFFICIAL ON PAPER. PROVEN IN THE FIELD.</h3>
          <dl className="mt-6 grid grid-cols-2 gap-y-4 text-[14px]">
            <dt className="text-[#8A96A3]">Company</dt><dd>{COMPANY.name}</dd>
            <dt className="text-[#8A96A3]">Registration</dt><dd>{COMPANY.reg}</dd>
            <dt className="text-[#8A96A3]">Established</dt><dd>2003 (23 years)</dd>
            <dt className="text-[#8A96A3]">Divisions</dt><dd>5 active divisions</dd>
            <dt className="text-[#8A96A3]">Export</dt><dd>Active across Africa</dd>
            <dt className="text-[#8A96A3]">Focus</dt><dd>100% B2B</dd>
          </dl>
        </div>
        <img src={IMAGES.warehouse} alt="Warehouse" className="mt-6 w-full h-[260px] object-cover" loading="lazy"/>
      </div>
    </div>
  </section>
);

const MissionSection = () => (
  <section className="bg-[#E8721C] py-16" data-testid="mission">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <div className="text-[12px] tracking-[.22em] font-bold text-[#0A1628] mb-3">OUR MISSION</div>
      <p className="font-display text-[28px] md:text-[42px] text-[#0A1628] leading-[1.05] max-w-5xl">
        "PROVIDING OUR CUSTOMERS WITH QUALITY PRODUCTS AND SERVICES THAT MEET INTERNATIONAL STANDARDS, COUPLED WITH SUPERIOR SERVICES AND PROMPT DELIVERY."
      </p>
    </div>
  </section>
);

const AccreditationsSection = () => (
  <section className="bg-[#F8F6F2] py-24" data-testid="accreditations">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading
        label="COMPLIANCE & CREDIBILITY"
        title="ACCREDITATIONS & REGISTRATIONS."
        subtitle="Verified, registered and compliant. The paperwork that lets serious clients work with us, no questions asked."
      />
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
        {ACCREDITATIONS.map(([t, d], i) => (
          <Reveal key={t} delay={i*70}>
            <div className="bg-white p-6 border-l-[3px] border-[#C8973C] h-full flex items-start gap-4" data-testid={`accred-${t.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
              <BadgeCheck className="text-[#C8973C] shrink-0 mt-1" size={28}/>
              <div>
                <div className="font-display text-[20px] text-[#0A1628] tracking-wide">{t}</div>
                <div className="text-[#1E2D40] text-[14px] leading-6 mt-1">{d}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-[#1E2D40]/70 text-[13px] italic">All compliance documents available on request.</p>
    </div>
  </section>
);

const TimelineSection = () => (
  <section className="bg-[#0A1628] py-24 blueprint-grid" data-testid="timeline">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading label="MILESTONES" title="23 YEARS, ONE TRAJECTORY." light />
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {MILESTONES.map(([y,t],i) => (
          <Reveal key={y} delay={i*60}>
            <div className="cg-card-dark" style={{background:"#1E2D40"}}>
              <div className="font-display text-[36px] text-[#E8721C] leading-none">{y}</div>
              <p className="mt-3 text-[#cbd3df] text-[15px] leading-6">{t}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ValuesSection = () => (
  <section className="bg-[#F8F6F2] py-24" data-testid="values">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading label="OUR VALUES" title="WHAT WE WON'T COMPROMISE ON." />
      <div className="grid md:grid-cols-3 gap-6">
        {VALUES.map(([n,d],i) => (
          <Reveal key={n} delay={i*100}><div className="cg-card h-full"><h4 className="font-display text-[24px] heading-bar">{n}</h4><p className="mt-4 text-[#1E2D40] leading-7">{d}</p></div></Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyDifferentSection = () => (
  <section className="bg-[#0A1628] py-24 text-white blueprint-grid" data-testid="why-different">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading label="WHY WE'RE DIFFERENT" title="THE 4 THINGS THAT SET US APART." light />
      <div className="grid md:grid-cols-2 gap-6">
        {DIFFERENTIATORS.map(([n,d],i) => (
          <Reveal key={n} delay={i*100}><div className="cg-card-dark" style={{background:"#1E2D40"}}><div className="flex items-start gap-4"><Check className="text-[#E8721C] mt-1" size={26}/><div><h4 className="font-display text-[22px]">{n}</h4><p className="mt-2 text-[#cbd3df] leading-7">{d}</p></div></div></div></Reveal>
        ))}
      </div>
      <div className="mt-12"><Link to="/contact" className="btn-orange">Talk to Us <ArrowRight size={14}/></Link></div>
    </div>
  </section>
);

const About = () => (
  <>
    <PageHero
      label="ABOUT CASSIE GEE"
      title="23 YEARS OF TRUSTED SUPPLY. BUILT ON QUALITY AND DELIVERY."
      subtitle="We started with a passion for quality supply. We built a company that leading hardware chains and international importers rely on."
      image={IMAGES.warehouse}
    />
    <StorySection />
    <MissionSection />
    <AccreditationsSection />
    <TimelineSection />
    <ValuesSection />
    <WhyDifferentSection />
  </>
);
export default About;
